// @flow
import * as React from 'react';
import EnableNotificationPrompt from './EnableNotificationPrompt';
import EnableNotifications from './EnableNotifications';
import { faCaretDown } from '@fortawesome/pro-solid-svg-icons/faCaretDown';
import { faBell as farBell } from '@fortawesome/pro-regular-svg-icons/faBell';
import { faBell as fasBell } from '@fortawesome/pro-solid-svg-icons/faBell';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { FormattedMessage } from 'react-intl';
import isEqual from 'react-fast-compare';
import Link from '@liveauctioneers/caterwaul-components/lib/Link/Link';
import Notification from './Notification';
import notificationPermissions from '../../../enums/notificationPermissions';
import Overlay from 'react-bootstrap/lib/Overlay';
import Popover from 'react-bootstrap/lib/Popover';
import styles from './NotificationDropDown.module.scss';

type DropDownMenuProps = {
    forceEnableNotificationsBanner: Function,
    markNotificationsDeleted: Function,
    markNotificationsRead: Function,
    mobile: string,
    notificationCount: number,
    notifications: any[],
    onEnter: Function,
    onEntered: Function,
    onExit: Function,
    onExited: Function,
    openMobileDownloadModal: Function,
    parent: any,
    permission: string,
    placement: string,
    requestBrowserPermission: string,
    setPermission: Function,
    trackView: Function,
};

type DropDownMenuState = {
    hasTracked: boolean,
    show: boolean,
};

export default class NotificationDropDownMenu extends React.Component<DropDownMenuProps, DropDownMenuState> {
    static defaultProps = {
        notificationCount: 0,
        notifications: [],
        onEnter: () => {},
        onEntered: () => {},
        onExit: () => {},
        onExited: () => {},
        permission: notificationPermissions.DEFAULT,
    };

    state = { hasTracked: false, show: false };

    shouldComponentUpdate = (nextProps: DropDownMenuProps, nextState: DropDownMenuState) =>
        !isEqual(this.props, nextProps) || !isEqual(this.state, nextState);

    targetRef: ?HTMLAnchorElement;

    open = () => {
        this.setState({ show: true });
        if (!this.state.hasTracked) {
            this.props.trackView();
            this.setState({ hasTracked: true });
        }
    };

    openAndMarkRead = () => {
        if (this.props.notificationCount > 0) {
            this.markAllNotificationsAsRead();
        }
        this.open();
    };

    close = () => {
        this.setState({ show: false });
    };

    closeAndMarkRead = () => {
        if (this.props.notificationCount > 0) {
            this.markAllNotificationsAsRead();
        }
        this.close();
    };

    toggle = () => {
        this.setState({ show: !this.state.show });
    };

    markAllNotificationsAsRead = () => {
        const notificationIds = this.props.notifications.map(n => n.id);
        this.props.markNotificationsRead(notificationIds);
    };

    markAllNotificationsAsDeleted = async () => {
        const notificationIds = this.props.notifications.map(n => n.id);
        this.props.markNotificationsDeleted(notificationIds);
    };

    openModal = () => {
        this.props.openMobileDownloadModal();
    };

    render() {
        const {
            forceEnableNotificationsBanner,
            mobile,
            markNotificationsDeleted,
            markNotificationsRead,
            onEnter,
            onEntered,
            onExit,
            onExited,
            notifications,
            notificationCount,
            parent,
            permission,
            placement,
            requestBrowserPermission,
            setPermission,
        } = this.props;
        const hasNewNotifications = notificationCount > 0;
        const hasNotifications = notifications.length > 0;
        const countStyle = styles.variantNotificationCount;
        const countStyles = hasNewNotifications ? countStyle : styles['notification-count-none'];
        const iconStyles = hasNewNotifications ? styles.icon : styles['icon-none'];
        const markAllReadStyles = hasNotifications ? styles['delete-all'] : styles['delete-all-disabled'];
        const buttonStyles = styles.variantNotificationButton;
        const isMobile = mobile === 'phone';
        return (
            <div className={buttonStyles}>
                <a
                    ref={ref => (this.targetRef = ref)}
                    onMouseEnter={!mobile ? this.open : () => {}}
                    onClick={this.toggle}
                    onMouseLeave={this.closeAndMarkRead}
                >
                    <div className={countStyles}>{notificationCount}</div>
                    <div className={styles.menuItemVariant}>
                        <FontAwesomeIcon icon={faCaretDown} className={styles.expander} />
                        <FontAwesomeIcon icon={hasNewNotifications ? fasBell : farBell} className={iconStyles} />
                        <span className={styles.menuText}>
                            <FormattedMessage id="userMenu.notifications" />
                        </span>
                    </div>
                </a>
                <Overlay
                    show={this.state.show}
                    className={styles.overlay}
                    onHide={this.close}
                    placement={placement}
                    target={this.targetRef}
                    container={parent}
                    onEnter={onEnter}
                    onEntered={onEntered}
                    onExit={onExit}
                    onExited={onExited}
                    rootClose
                >
                    <Popover
                        className={styles.popover}
                        id="popover-trigger-click-root-close"
                        onMouseOver={this.open}
                        onMouseOut={this.close}
                    >
                        <span className={styles['delete-all-span']}>
                            <Link className={markAllReadStyles} onClick={this.markAllNotificationsAsDeleted}>
                                <FormattedMessage id="notifications.delete_all" />
                            </Link>
                        </span>
                        <hr />
                        <EnableNotificationPrompt
                            forceEnableNotificationsBanner={forceEnableNotificationsBanner}
                            hasNotifications={hasNotifications}
                            permission={permission}
                            requestBrowserPermission={requestBrowserPermission}
                        />
                        {hasNotifications ? (
                            <div className={styles.notifications}>
                                {notifications.map((n, idx) => {
                                    return (
                                        <Notification
                                            id={n.id}
                                            imageUrl={n.imageUrl}
                                            isNew={idx < notificationCount}
                                            key={n.id}
                                            link={n.link}
                                            markNotificationsDeleted={markNotificationsDeleted}
                                            markNotificationsRead={markNotificationsRead}
                                            message={n.message}
                                            messageLevel={n.messageLevel}
                                            timestamp={n.timestamp}
                                            title={n.title}
                                        />
                                    );
                                })}
                            </div>
                        ) : (
                            <div>
                                <div className={styles['empty-state']}>
                                    <FormattedMessage id="notifications.no_new_notifications" />
                                </div>
                            </div>
                        )}
                        {isMobile ? (
                            <div className={styles['download-link']}>
                                <FormattedMessage id="notifications.get_notifications_phone" />
                                <br />
                                <Link onClick={this.openModal}>
                                    <FormattedMessage id="notifications.download_app" />
                                </Link>
                            </div>
                        ) : (
                            <div className={styles['download-link']}>
                                <EnableNotifications
                                    forceEnableNotificationsBanner={forceEnableNotificationsBanner}
                                    requestBrowserPermission={requestBrowserPermission}
                                    permission={permission}
                                    setPermission={setPermission}
                                />
                            </div>
                        )}
                    </Popover>
                </Overlay>
            </div>
        );
    }
}
